/* PARTNER INVITE — public landing page off a pump company's or contractor's link.
   Two ways in: create a free dispatch-only account, or connect the plant you already run. */
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Handshake, Construction, Truck, ShieldCheck, Check, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PartnerInviteLanding() {
  const { token } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [mode, setMode] = useState("new");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [f, setF] = useState({ company: "", name: "", email: "", phone: "", password: "", country: "" });

  useEffect(() => {
    axios.get(`${API}/public/partner-invite/${token}`)
      .then((r) => setInfo(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "This invite link is not valid"));
  }, [token]);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const signup = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/partner-invite/${token}/signup`, f);
      setDone(data);
    } catch (ex) { toast.error(ex.response?.data?.detail || "Could not create the account"); }
    finally { setBusy(false); }
  };

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/partner-invite/${token}/connect`);
      toast.success(data.already ? "Already connected" : `Connected to ${data.partner.name}`);
      nav("/partners");
    } catch (ex) { toast.error(ex.response?.data?.detail || "Could not connect — you need plant admin rights"); }
    finally { setBusy(false); }
  };

  const Icon = info?.partner?.kind === "pump" ? Construction : Truck;
  const field = "w-full bg-black/40 border border-zinc-700 focus:border-[#F57C00] outline-none text-white px-3 py-2.5 mono text-sm";

  return (
    <div className="contractor-theme min-h-screen bg-[#0B0D10] text-zinc-200 flex items-center justify-center px-4 py-10" data-testid="partner-invite-page">
      <div className="w-full max-w-2xl">
        <div className="mono text-[10px] tracking-[0.35em] text-zinc-500 mb-2">ARMORBATCH GLOBAL · PARTNER NETWORK</div>

        {err && <div className="border border-red-800 bg-red-950/40 text-red-300 p-4 mono text-sm" data-testid="partner-invite-error">{err}</div>}

        {!info && !err && <div className="flex items-center gap-2 text-zinc-500 mono text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Checking the link…</div>}

        {info && !done && (
          <>
            <div className="border border-zinc-800 bg-[#11141A] p-5 sm:p-7">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#38BDF8] flex items-center justify-center shrink-0"><Icon className="w-6 h-6 text-black" /></div>
                <div>
                  <h1 className="heading text-2xl sm:text-3xl font-black uppercase !text-white leading-tight" data-testid="partner-invite-name">
                    {info.partner.name}
                  </h1>
                  <p className="mono text-sm text-zinc-400 mt-1">
                    wants to connect as your {info.partner.kind === "pump" ? "pump partner" : "haulage partner"} on ArmorBatch.
                  </p>
                </div>
              </div>
              <div className="mt-5 border-l-2 border-[#38BDF8] pl-4 mono text-[13px] leading-relaxed text-zinc-300">
                <div className="flex items-center gap-2 text-emerald-400 uppercase tracking-widest text-[10px] font-black mb-2"><ShieldCheck className="w-3.5 h-3.5" /> what they can see</div>
                Only the jobs their own equipment is booked on: site, time, m³, mix code, which truck is
                running, its live position and the ETA to site, plus the time each load was batched.
                <div className="text-zinc-500 mt-2">Never your customers, your prices, your other partners or any other job.</div>
              </div>
            </div>

            <div className="flex mt-6 border border-zinc-800">
              {[["new", "I'm new — create a free account"], ["existing", "I already run ArmorBatch"]].map(([k, label]) => (
                <button key={k} type="button" onClick={() => setMode(k)} data-testid={`partner-invite-tab-${k}`}
                  className={`flex-1 mono text-[11px] uppercase tracking-widest font-black px-3 py-3 ${mode === k ? "bg-[#F57C00] text-black" : "text-zinc-400 hover:text-white"}`}>
                  {label}
                </button>
              ))}
            </div>

            {mode === "new" ? (
              <form onSubmit={signup} className="border border-t-0 border-zinc-800 bg-[#11141A] p-5 sm:p-7 space-y-3" data-testid="partner-invite-signup-form">
                <p className="mono text-[12px] text-zinc-400">
                  Free dispatch account: your day's jobs, your trucks, your drivers and the driver app with live GPS.
                  Sharing with partners is free for the first {info.share_trial_days} days.
                </p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <input required placeholder="Concrete company" value={f.company} onChange={set("company")} data-testid="partner-signup-company" className={field} />
                  <input required placeholder="Your name" value={f.name} onChange={set("name")} data-testid="partner-signup-name" className={field} />
                  <input required type="email" placeholder="Work email" value={f.email} onChange={set("email")} data-testid="partner-signup-email" className={field} />
                  <input required placeholder="Mobile" value={f.phone} onChange={set("phone")} data-testid="partner-signup-phone" className={field} />
                  <input required type="password" placeholder="Password (8+ with a number)" value={f.password} onChange={set("password")} data-testid="partner-signup-password" className={field} />
                  <input placeholder="Country (optional)" value={f.country} onChange={set("country")} data-testid="partner-signup-country" className={field} />
                </div>
                <button type="submit" disabled={busy} data-testid="partner-signup-submit"
                  className="w-full mono uppercase tracking-widest font-black bg-[#F57C00] text-black py-3.5 hover:bg-[#ff8f1f] disabled:opacity-50 inline-flex items-center justify-center gap-2">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Handshake className="w-4 h-4" />} Create account & connect
                </button>
              </form>
            ) : (
              <div className="border border-t-0 border-zinc-800 bg-[#11141A] p-5 sm:p-7 space-y-4" data-testid="partner-invite-connect">
                {user ? (
                  <>
                    <p className="mono text-[13px] text-zinc-300">
                      Signed in as <span className="text-white font-black">{user.name}</span>. Connect your plant to{" "}
                      <span className="text-[#38BDF8] font-black">{info.partner.name}</span>?
                    </p>
                    <div className="flex gap-3 flex-wrap">
                      <button type="button" onClick={connect} disabled={busy} data-testid="partner-connect-accept"
                        className="mono uppercase tracking-widest font-black bg-emerald-500 text-black px-7 py-3.5 hover:bg-emerald-400 disabled:opacity-50 inline-flex items-center gap-2">
                        <Check className="w-4 h-4" /> Accept & connect
                      </button>
                      <button type="button" onClick={() => nav("/")} data-testid="partner-connect-decline"
                        className="mono uppercase tracking-widest font-black bg-red-600 text-white px-7 py-3.5 hover:bg-red-700">✕ Decline</button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="mono text-[13px] text-zinc-300">Sign in to your plant first — then come back to this link and accept.</p>
                    <button type="button" onClick={() => { localStorage.setItem("armorbatch.pendingInvite", token); nav("/login"); }}
                      data-testid="partner-connect-login" className="mono uppercase tracking-widest font-black bg-[#38BDF8] text-black px-7 py-3.5 hover:bg-sky-300">Sign in</button>
                  </>
                )}
              </div>
            )}
          </>
        )}

        {done && (
          <div className="border border-emerald-700 bg-emerald-950/30 p-7 text-center" data-testid="partner-invite-done">
            <Check className="w-10 h-10 mx-auto text-emerald-400" />
            <h2 className="heading text-2xl font-black uppercase !text-white mt-3">You're connected</h2>
            <p className="mono text-sm text-zinc-300 mt-2">
              {done.partner_name} can now see live loads for jobs their equipment is on. Sign in with{" "}
              <span className="text-white font-black">{done.email}</span>.
            </p>
            <button type="button" onClick={() => nav("/login")} data-testid="partner-invite-signin"
              className="mt-5 mono uppercase tracking-widest font-black bg-[#F57C00] text-black px-8 py-3 hover:bg-[#ff8f1f]">Sign in</button>
          </div>
        )}
      </div>
    </div>
  );
}
